import { useQuery } from '@tanstack/react-query';
import { adminApi, type CostPeriod } from '../../../api/adminApi';

interface CostsTabProps {
  userId: string;
}

export function CostsTab({ userId }: CostsTabProps) {
  const { data: userCosts, isLoading: costsLoading } = useQuery({
    queryKey: ['userCosts', userId],
    queryFn: () => adminApi.getUserCosts(userId),
    enabled: !!userId,
  });

  return (
    <div className="features-section">
      {costsLoading ? (
        <div className="loading">Loading costs...</div>
      ) : !userCosts ? (
        <p className="no-features-message">No cost data available.</p>
      ) : (
        <CostsTabContent last7Days={userCosts.last7Days} allTime={userCosts.allTime} />
      )}
    </div>
  );
}

function CostsTabContent({ last7Days, allTime }: { last7Days: CostPeriod; allTime: CostPeriod }) {
  const formatCost = (usd: number) => usd < 0.01 && usd > 0 ? '<$0.01' : `$${usd.toFixed(2)}`;
  const formatTokens = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : String(n);

  const renderPeriod = (label: string, period: CostPeriod) => (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 15, margin: '0 0 12px 0', color: 'var(--text-primary)' }}>{label}</h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div style={{ background: 'var(--bg-tertiary)', padding: 16, borderRadius: 8 }}>
          <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 4 }}>Submissions</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)' }}>{period.submissions}</div>
        </div>
        <div style={{ background: 'var(--bg-tertiary)', padding: 16, borderRadius: 8 }}>
          <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 4 }}>LLM Calls</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)' }}>{period.llmCalls}</div>
        </div>
        <div style={{ background: 'var(--bg-tertiary)', padding: 16, borderRadius: 8 }}>
          <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 4 }}>Est. Cost</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)' }}>{formatCost(period.totalCostUsd)}</div>
        </div>
      </div>

      {period.byProvider.length > 0 && (
        <table className="dt dt--compact" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ padding: '6px 10px' }}>Provider</th>
              <th style={{ padding: '6px 10px', textAlign: 'right' }}>Calls</th>
              <th style={{ padding: '6px 10px', textAlign: 'right' }}>Input Tokens</th>
              <th style={{ padding: '6px 10px', textAlign: 'right' }}>Output Tokens</th>
              <th style={{ padding: '6px 10px', textAlign: 'right' }}>Total Tokens</th>
              <th style={{ padding: '6px 10px', textAlign: 'right' }}>Est. Cost</th>
            </tr>
          </thead>
          <tbody>
            {period.byProvider.map(p => (
              <tr key={p.provider}>
                <td style={{ padding: '6px 10px', fontWeight: 500 }}>{p.provider}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{p.calls}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTokens(p.inputTokens)}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTokens(p.outputTokens)}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTokens(p.totalTokens)}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 500 }}>{formatCost(p.costUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <>
      {renderPeriod('Last 7 Days', last7Days)}
      {renderPeriod('All Time', allTime)}
    </>
  );
}
